import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import type { IconData } from './icon-data.js';
import { Icon } from './Icon.js';
import { LoadingIndicator } from './LoadingIndicator.js';
import { cx } from './util.js';

export type ButtonVariant = 'filled' | 'tonal' | 'outlined' | 'text';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  /** 40 dp or 56 dp high. Both keep a 48 dp hit area. */
  size?: 'md' | 'lg';
  icon?: IconData;
  /** Shows the loading indicator and refuses taps, without changing the button's width. */
  loading?: boolean;
  /** Fills the width. Strings are long in Marathi and Hindi, so buttons stack instead of truncating. */
  block?: boolean;
  destructive?: boolean;
  /** Part of a connected group: the outer corners are round, the inner ones small. */
  children: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'filled',
    size = 'md',
    icon,
    loading = false,
    block = false,
    destructive = false,
    className,
    children,
    disabled,
    type = 'button',
    onClick,
    ...rest
  },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={loading ? undefined : onClick}
      className={cx(
        'k-btn k-layer k-t-label-lg',
        `k-btn--${variant}`,
        `k-btn--${size}`,
        block && 'k-btn--block',
        destructive && 'k-btn--destructive',
        loading && 'k-btn--loading',
        className,
      )}
    >
      {loading ? (
        <LoadingIndicator size={20} label="" />
      ) : icon ? (
        <Icon icon={icon} size={20} />
      ) : null}
      <span className="k-btn__label">{children}</span>
    </button>
  );
});

export interface ButtonGroupProps {
  children: ReactNode;
  label: string;
  className?: string;
}

/** Buttons joined with 2 dp gaps; the outer corners are round, the inner ones small. */
export function ButtonGroup({ children, label, className }: ButtonGroupProps) {
  return (
    <div role="group" aria-label={label} className={cx('k-btn-group', className)}>
      {children}
    </div>
  );
}
